import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface AIProvider {
  id: number;
  name: string;
  type: string;
  apiKey: string;
  hasApiKey?: boolean;
  apiKeyRevealed?: boolean;
  baseUrl?: string;
  isActive: boolean;
  maxTokens?: number;
  temperature?: number;
  description?: string;
  hasModule?: boolean;
  moduleFileName?: string | null;
  moduleClassName?: string | null;
  isCatalogOnly?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface LlmModuleInfo {
  type: string;
  label: string;
  hasModule: boolean;
  moduleFileName?: string | null;
  moduleClassName?: string | null;
}

type ListRow = AIProvider & { listKey: string };

export function AIProviders() {
  const { accessToken } = useAuthStore();
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [modules, setModules] = useState<LlmModuleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [providersRes, modulesRes] = await Promise.all([
        fetch('/api/admin/ai-providers', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch('/api/admin/ai-provider-modules', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (!providersRes.ok) throw new Error('Failed to fetch AI providers');
      if (!modulesRes.ok) throw new Error('Failed to fetch AI provider modules');

      setProviders(await providersRes.json());
      setModules(await modulesRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI providers');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows: ListRow[] = useMemo(() => {
    const configured = providers.map((p) => ({
      ...p,
      listKey: `cfg-${p.id}`,
      isCatalogOnly: false,
    }));

    const configuredTypes = new Set(providers.map((p) => p.type.toLowerCase()));
    const catalogExtras = modules
      .filter((m) => !configuredTypes.has(m.type.toLowerCase()))
      .map((m) => ({
        id: 0,
        name: m.label,
        type: m.type,
        apiKey: '',
        isActive: false,
        description: m.hasModule
          ? `Modül hazır (${m.moduleFileName}); henüz yapılandırılmamış.`
          : 'LLM modül dosyası yok; henüz implement edilmedi.',
        hasModule: m.hasModule,
        moduleFileName: m.moduleFileName,
        moduleClassName: m.moduleClassName,
        isCatalogOnly: true,
        listKey: `cat-${m.type}`,
      }));

    return [...configured, ...catalogExtras];
  }, [providers, modules]);

  const providerHasApiKey = (provider: AIProvider): boolean =>
    provider.hasApiKey === true || (Boolean(provider.apiKey) && !/^•+$/.test(provider.apiKey));

  const handleToggleActive = async (provider: AIProvider) => {
    if (provider.isCatalogOnly || !provider.id) return;
    setActionLoading(provider.id);
    setError('');

    try {
      const response = await fetch(`/api/admin/ai-providers/${provider.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to toggle provider status');
      }

      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle provider status');
    } finally {
      setActionLoading(null);
    }
  };

  const getProviderTypeLabel = (type: string): string => {
    return modules.find((m) => m.type === type)?.label || type;
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>AI Providers</h1>
          <p>AI sağlayıcılarını yönetin; modül dosyası olmayan tipler de listede görünür</p>
        </div>
        <Link className="admin-btn admin-btn-primary" to="/admin/ai-providers/new">
          + Yeni Provider Ekle
        </Link>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>AI Providers ({rows.length})</h2>
        </div>

        {rows.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz AI provider tanımlanmamış.</p>
            <Link className="admin-btn admin-btn-primary" to="/admin/ai-providers/new">
              İlk Provider'ı Ekle
            </Link>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>Modül</th>
                  <th>Base URL</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((provider) => (
                  <tr
                    key={provider.listKey}
                    className={provider.isCatalogOnly ? 'admin-row-muted' : undefined}
                  >
                    <td>
                      <div className="provider-info">
                        <div className="provider-name-row">
                          <span className="provider-name">{provider.name}</span>
                          {provider.isCatalogOnly && (
                            <span className="provider-catalog-badge" title="Katalog kaydı">
                              Katalog
                            </span>
                          )}
                          {!provider.isCatalogOnly && !providerHasApiKey(provider) && (
                            <span
                              className="provider-missing-api-key-badge"
                              title="API Key tanımlı değil"
                            >
                              <ExclamationTriangleIcon aria-hidden="true" />
                              API Key yok
                            </span>
                          )}
                        </div>
                        {provider.description && (
                          <span className="provider-description">{provider.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="provider-type-badge">
                        {getProviderTypeLabel(provider.type)}
                      </span>
                    </td>
                    <td>
                      {provider.hasModule ? (
                        <span
                          className="provider-module-badge provider-module-badge-ok"
                          title={provider.moduleFileName || undefined}
                        >
                          {provider.moduleFileName || 'Var'}
                        </span>
                      ) : (
                        <span className="provider-module-badge provider-module-badge-missing">
                          Dosya yok
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="provider-base-url">
                        {provider.isCatalogOnly ? '—' : provider.baseUrl || '-'}
                      </span>
                    </td>
                    <td>
                      {provider.isCatalogOnly ? (
                        <span className="admin-muted-text">Yapılandırılmamış</span>
                      ) : (
                        <button
                          className={`admin-toggle-btn ${provider.isActive ? 'active' : ''}`}
                          onClick={() => handleToggleActive(provider)}
                          disabled={actionLoading === provider.id}
                          title={provider.isActive ? 'Deaktif Et' : 'Aktif Et'}
                        >
                          {provider.isActive ? '✓ Aktif' : '✗ Pasif'}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        {provider.isCatalogOnly ? (
                          <Link
                            className="admin-btn admin-btn-secondary admin-btn-sm"
                            to={`/admin/ai-providers/new?type=${encodeURIComponent(provider.type)}`}
                          >
                            Yapılandır
                          </Link>
                        ) : (
                          <Link
                            className="admin-edit-btn"
                            to={`/admin/ai-providers/${provider.id}`}
                            title="Detay"
                          >
                            ✏️
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
